/**
 * The dice as pure mathematics: vertices, faces, numerals. No WebGL, no DOM.
 *
 * Ported from Armarium (`apps/web/src/lib/dice3d/geometry.ts`). Every body
 * comes out of the same machinery: a vertex set, its convex hull, and the
 * numerals laid so opposite faces sum to `sides + 1`. The icosahedron is what
 * the initiative roll-off throws; the board spawns all six.
 *
 * Convention: "up" is +Y and the camera looks down at the table. The rolled
 * face is the one whose normal points at +Y; its numeral's up direction points
 * at -Z so the number reads upright on screen.
 *
 * The d4 is the odd one out. A tetrahedron never rests with a face up, so a
 * real one prints three numerals per face, one at each corner, and the result
 * is the corner pointing at the ceiling. Here the *values sit on the vertices*
 * and "face `i`" means "vertex `i` up". `faces[i]` is the face opposite vertex
 * `i`, so `faceIndexForValue` and the atlas still index one cell per face.
 */

export const vAdd = (a, b) => [a[0] + b[0], a[1] + b[1], a[2] + b[2]];
export const vSub = (a, b) => [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
export const vScale = (a, s) => [a[0] * s, a[1] * s, a[2] * s];
export const vDot = (a, b) => a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
export const vCross = (a, b) => [
  a[1] * b[2] - a[2] * b[1],
  a[2] * b[0] - a[0] * b[2],
  a[0] * b[1] - a[1] * b[0],
];
export const vLength = (a) => Math.sqrt(vDot(a, a));

export function vNormalize(a) {
  const l = vLength(a);
  return l > 0 ? vScale(a, 1 / l) : [0, 0, 1];
}

export const qIdentity = () => ({ w: 1, x: 0, y: 0, z: 0 });

export function qAxisAngle(axis, angle) {
  const h = angle / 2;
  const s = Math.sin(h);
  return { w: Math.cos(h), x: axis[0] * s, y: axis[1] * s, z: axis[2] * s };
}

export function qMul(a, b) {
  return {
    w: a.w * b.w - a.x * b.x - a.y * b.y - a.z * b.z,
    x: a.w * b.x + a.x * b.w + a.y * b.z - a.z * b.y,
    y: a.w * b.y - a.x * b.z + a.y * b.w + a.z * b.x,
    z: a.w * b.z + a.x * b.y - a.y * b.x + a.z * b.w,
  };
}

export function qNormalize(q) {
  const l = Math.sqrt(q.w * q.w + q.x * q.x + q.y * q.y + q.z * q.z) || 1;
  return { w: q.w / l, x: q.x / l, y: q.y / l, z: q.z / l };
}

/** Spherical interpolation. `t` may exceed 1. The rotation then overshoots. */
export function qSlerp(a, b, t) {
  let d = a.w * b.w + a.x * b.x + a.y * b.y + a.z * b.z;
  let bw = b.w;
  let bx = b.x;
  let by = b.y;
  let bz = b.z;
  if (d < 0) {
    d = -d;
    bw = -bw;
    bx = -bx;
    by = -by;
    bz = -bz;
  }
  if (d > 0.9995) {
    return qNormalize({
      w: a.w + (bw - a.w) * t,
      x: a.x + (bx - a.x) * t,
      y: a.y + (by - a.y) * t,
      z: a.z + (bz - a.z) * t,
    });
  }
  const theta = Math.acos(Math.min(1, d));
  const s = Math.sin(theta);
  const fa = Math.sin((1 - t) * theta) / s;
  const fb = Math.sin(t * theta) / s;
  return qNormalize({
    w: fa * a.w + fb * bw,
    x: fa * a.x + fb * bx,
    y: fa * a.y + fb * by,
    z: fa * a.z + fb * bz,
  });
}

/**
 * Quaternion from a rotation matrix given as three rows. The matrix maps
 * `right -> +X`, `up -> +Y`, `normal -> +Z`. Shepperd's case split, so nothing
 * degenerates when the trace approaches -1.
 */
function qFromRows(r0, r1, r2) {
  const trace = r0[0] + r1[1] + r2[2];
  if (trace > 0) {
    const s = Math.sqrt(trace + 1) * 2;
    return qNormalize({
      w: s / 4,
      x: (r2[1] - r1[2]) / s,
      y: (r0[2] - r2[0]) / s,
      z: (r1[0] - r0[1]) / s,
    });
  }
  if (r0[0] > r1[1] && r0[0] > r2[2]) {
    const s = Math.sqrt(1 + r0[0] - r1[1] - r2[2]) * 2;
    return qNormalize({
      w: (r2[1] - r1[2]) / s,
      x: s / 4,
      y: (r0[1] + r1[0]) / s,
      z: (r0[2] + r2[0]) / s,
    });
  }
  if (r1[1] > r2[2]) {
    const s = Math.sqrt(1 + r1[1] - r0[0] - r2[2]) * 2;
    return qNormalize({
      w: (r0[2] - r2[0]) / s,
      x: (r0[1] + r1[0]) / s,
      y: s / 4,
      z: (r1[2] + r2[1]) / s,
    });
  }
  const s = Math.sqrt(1 + r2[2] - r0[0] - r1[1]) * 2;
  return qNormalize({
    w: (r1[0] - r0[1]) / s,
    x: (r0[2] + r2[0]) / s,
    y: (r1[2] + r2[1]) / s,
    z: s / 4,
  });
}

const PHI = (1 + Math.sqrt(5)) / 2;

/** Any unit vector perpendicular to n. */
function perpendicular(n) {
  const pick = Math.abs(n[0]) < 0.9 ? [1, 0, 0] : [0, 1, 0];
  return vNormalize(vCross(n, pick));
}

/**
 * The faces of the convex hull, from the vertices alone. No lookup table.
 *
 * Any plane through three vertices that leaves every remaining vertex on its
 * inner side is a face. That is the supporting-plane definition of the convex
 * hull; with twenty vertices the cubic cost is irrelevant and it is computed
 * once and cached.
 */
function hullFaces(vertices) {
  const EPS = 1e-6;
  const found = [];
  const n = vertices.length;

  for (let i = 0; i < n; i++) {
    for (let j = i + 1; j < n; j++) {
      for (let k = j + 1; k < n; k++) {
        const raw = vCross(vSub(vertices[j], vertices[i]), vSub(vertices[k], vertices[i]));
        if (vLength(raw) < EPS) continue;

        let normal = vNormalize(raw);
        let offset = vDot(normal, vertices[i]);
        if (offset < 0) {
          normal = vScale(normal, -1);
          offset = -offset;
        }
        if (offset < EPS) continue;
        if (!vertices.every((v) => vDot(normal, v) <= offset + EPS)) continue;
        if (found.some((f) => vDot(f.normal, normal) > 0.9999)) continue;

        found.push({
          normal,
          indices: vertices
            .map((v, index) => ({ v, index }))
            .filter(({ v }) => Math.abs(vDot(normal, v) - offset) < EPS)
            .map(({ index }) => index),
        });
      }
    }
  }

  // Order each face's vertices around its own normal, so it is a polygon and
  // not a star.
  return found.map(({ normal, indices }) => {
    const e1 = perpendicular(normal);
    const e2 = vCross(normal, e1);
    return [...indices].sort(
      (a, b) => Math.atan2(vDot(vertices[a], e2), vDot(vertices[a], e1))
        - Math.atan2(vDot(vertices[b], e2), vDot(vertices[b], e1)),
    );
  });
}

/** Newell's face normal. Stable for any planar polygon. */
function newellNormal(vertices, face) {
  let x = 0;
  let y = 0;
  let z = 0;
  for (let i = 0; i < face.length; i++) {
    const a = vertices[face[i]];
    const b = vertices[face[(i + 1) % face.length]];
    x += (a[1] - b[1]) * (a[2] + b[2]);
    y += (a[2] - b[2]) * (a[0] + b[0]);
    z += (a[0] - b[0]) * (a[1] + b[1]);
  }
  return vNormalize([x, y, z]);
}

function faceCenter(vertices, face) {
  let c = [0, 0, 0];
  for (const i of face) c = vAdd(c, vertices[i]);
  return vScale(c, 1 / face.length);
}

/** The twelve icosahedron vertices, on a circumsphere of radius 1. */
function icosahedronVertices() {
  const vertices = [];
  for (const a of [1, -1]) {
    for (const b of [PHI, -PHI]) {
      vertices.push(vNormalize([0, a, b]));
      vertices.push(vNormalize([a, b, 0]));
      vertices.push(vNormalize([b, 0, a]));
    }
  }
  return vertices;
}

/** Alternate corners of a cube: a regular tetrahedron. */
function tetrahedronVertices() {
  return [[1, 1, 1], [1, -1, -1], [-1, 1, -1], [-1, -1, 1]].map(vNormalize);
}

function cubeVertices() {
  const vertices = [];
  for (const x of [1, -1]) for (const y of [1, -1]) for (const z of [1, -1]) {
    vertices.push(vNormalize([x, y, z]));
  }
  return vertices;
}

function octahedronVertices() {
  return [[1, 0, 0], [-1, 0, 0], [0, 1, 0], [0, -1, 0], [0, 0, 1], [0, 0, -1]];
}

/**
 * The pentagonal trapezohedron. Ten ring vertices alternate between two
 * heights, one pole at each end. The pole height is not free: a kite is only
 * planar when the apex sits exactly where the plane through a high ring vertex
 * and its two low neighbours meets the axis, which pins `T` to `h`.
 */
function trapezohedronVertices() {
  const h = 0.105;
  const c = Math.cos(Math.PI / 5);
  const apex = h * (1 + c) / (1 - c);
  const vertices = [[0, apex, 0], [0, -apex, 0]];
  for (let i = 0; i < 10; i++) {
    const a = (i / 10) * Math.PI * 2;
    vertices.push([Math.cos(a), i % 2 === 0 ? h : -h, Math.sin(a)]);
  }
  return vertices;
}

function dodecahedronVertices() {
  const vertices = [];
  const r = 1 / PHI;
  for (const x of [1, -1]) for (const y of [1, -1]) for (const z of [1, -1]) {
    vertices.push([x, y, z]);
  }
  for (const a of [1, -1]) for (const b of [1, -1]) {
    vertices.push([0, a * r, b * PHI]);
    vertices.push([a * r, b * PHI, 0]);
    vertices.push([a * PHI, 0, b * r]);
  }
  return vertices.map(vNormalize);
}

const VERTEX_SETS = {
  4: tetrahedronVertices,
  6: cubeVertices,
  8: octahedronVertices,
  10: trapezohedronVertices,
  12: dodecahedronVertices,
  20: icosahedronVertices,
};

export const DIE_SIDES = Object.keys(VERTEX_SETS).map(Number);

/** Numbers laid out so opposite faces sum to `n + 1`, as on a real die. */
function assignValues(normals) {
  const n = normals.length;
  const values = new Array(n).fill(0);
  let next = 1;
  for (let i = 0; i < n; i++) {
    if (values[i] !== 0) continue;
    const opposite = normals.findIndex((m, j) => j !== i && vDot(normals[i], m) < -0.999);
    values[i] = next;
    values[opposite] = n + 1 - next;
    next++;
  }
  return values;
}

/**
 * Where the numeral's head points on a face. A triangle's first vertex is a
 * corner and works directly. A square reads along its edges, so the head goes
 * to an edge midpoint. A kite reads toward its sharp tip, the one nearest the
 * axis. A pentagon reads toward a corner.
 */
function numeralUp(vertices, face, center, normal, sides) {
  let target;
  if (sides === 6) {
    target = vScale(vAdd(vertices[face[0]], vertices[face[1]]), 0.5);
  } else if (sides === 10) {
    target = vertices[face.reduce((best, vi) => (
      Math.abs(vertices[vi][1]) > Math.abs(vertices[best][1]) ? vi : best
    ), face[0])];
  } else {
    target = vertices[face[0]];
  }
  const raw = vSub(target, center);
  return vNormalize(vSub(raw, vScale(normal, vDot(raw, normal))));
}

const cache = new Map();

/**
 * The body: vertices, faces, per-face normals, centers, printed values and
 * numeral-up directions. Computed once per side count.
 */
export function dieGeometry(sides) {
  const hit = cache.get(sides);
  if (hit !== undefined) return hit;
  const makeVertices = VERTEX_SETS[sides];
  if (makeVertices === undefined) throw new Error(`d${sides}: no such die`);

  const vertices = makeVertices();
  let faces = hullFaces(vertices);
  if (faces.length !== sides) {
    throw new Error(`d${sides}: the hull found ${faces.length} faces`);
  }
  // Every face wound counter-clockwise as seen from outside.
  for (const face of faces) {
    if (vDot(newellNormal(vertices, face), faceCenter(vertices, face)) < 0) face.reverse();
  }
  const vertexUp = sides === 4;
  if (vertexUp) {
    // Face `i` is the one opposite vertex `i`: the face on the table when
    // vertex `i` points up.
    faces = vertices.map((_, vi) => faces.find((f) => !f.includes(vi)));
  }
  const normals = faces.map((f) => newellNormal(vertices, f));
  const centers = faces.map((f) => faceCenter(vertices, f));
  const ups = faces.map((f, i) => numeralUp(vertices, f, centers[i], normals[i], sides));
  const values = vertexUp ? vertices.map((_, i) => i + 1) : assignValues(normals);

  const geometry = { sides, vertexUp, vertices, faces, values, normals, centers, ups };
  cache.set(sides, geometry);
  return geometry;
}

export function d20Geometry() {
  return dieGeometry(20);
}

/**
 * The orientation in which `face` lies on top with its numeral upright when
 * read from above (head toward -Z). This is what a throw runs toward.
 */
export function faceQuaternion(geometry, face) {
  if (geometry.vertexUp) return vertexQuaternion(geometry, face);
  const n = geometry.normals[face];
  const u = geometry.ups[face];
  const t = vCross(u, n);
  // Right-handed: right -> +X, normal -> +Y, numeral-up -> -Z.
  return qFromRows(t, n, vScale(u, -1));
}

/**
 * The d4 result pose: vertex `index` at the ceiling, and one of the three
 * faces around it leaning toward the camera (+Z) so its corner numeral reads.
 */
function vertexQuaternion(geometry, index) {
  const n = vNormalize(geometry.vertices[index]);
  const lean = geometry.faces.findIndex((f) => f.includes(index));
  const raw = geometry.centers[lean];
  const u = vNormalize(vSub(raw, vScale(n, vDot(raw, n))));
  // Right-handed: (n x u) -> +X, vertex -> +Y, leaning face -> +Z.
  return qFromRows(vCross(n, u), n, u);
}

export function faceIndexForValue(geometry, value) {
  return geometry.values.indexOf(value);
}

/**
 * How the die comes to rest. Not dead flat, but tilted a little.
 *
 * A body whose face points exactly up is no longer a body when seen from
 * above, it is a polygon. A little tilt shows the neighbouring facets. The
 * amount is a fixed share of the angle to the nearest other face, so the
 * rolled face stays unambiguously the front-most one.
 */
const TILT_SHARE = 0.3;

export function restingQuaternion(geometry, face) {
  // Vertex-up bodies already show three facets; a tilt would only hide one.
  if (geometry.vertexUp) return faceQuaternion(geometry, face);
  const n = geometry.normals[face];
  let nearest = Math.PI;
  for (let i = 0; i < geometry.normals.length; i++) {
    if (i === face) continue;
    nearest = Math.min(nearest, Math.acos(Math.min(1, Math.max(-1, vDot(n, geometry.normals[i])))));
  }
  const angle = nearest * TILT_SHARE;
  // Tilted about the horizontal axes: a touch toward the camera and a hair to
  // the side.
  const tilt = qMul(qAxisAngle([0, 0, 1], angle * 0.62), qAxisAngle([1, 0, 0], angle * 0.78));
  return qNormalize(qMul(tilt, faceQuaternion(geometry, face)));
}
